/**
 * @author Owais Liaquat Ali
 * @datetime May, 23th 2022
 * @description: This is add User details that is responsible for every childs components actions
 */
import {
  convertDateTimeFormat,
  convertParseDateTime,
  removeSpecialChar,
  resetForm,
  transformPhone,
} from 'src/app/shared/utils/utility.service';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { AccessDetailEmailComponent } from 'src/app/user-management/add-user/access-detail-email/access-detail-email.component';
import { AccessTimeComponent } from 'src/app/user-management/add-user/access-time/access-time.component';
import { UserDetailsComponent } from 'src/app/user-management/add-user/user-details/user-details.component';
import {
  IAccessDetail,
  IShareAccess,
  IUnitAndAccessPoint,
  IUnitDetail,
  IUser,
  IUserGroupDetails,
} from 'src/app/user-management/user-management-interfaces';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { ISharedData } from 'src/app/shared/state-management/state-interfaces';
import { UserManagementService } from 'src/app/user-management/user-management.service';
import { MFValidators } from 'src/app/shared/validators/mf-validators';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { UserTypeComponent } from 'src/app/user-management/add-user/user-type/user-type.component';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { UserGroupComponent } from 'src/app/user-management/user-group/user-group.component';
import { ConfirmDialogComponent } from 'src/app/shared-layout/Components/confirm-dialog/confirm-dialog.component';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { BuildingManagementService } from '../../building-management.service';
import { VerifyEmailComponent } from 'src/app/user-management/add-user/verify-email/verify-email.component';
import { UserTypeRequest } from 'src/app/shared/constants/request-mapping';
import { userTypeResponse } from 'src/app/shared/constants/response-mapping';
import { AddUserService } from 'src/app/user-management/add-user/add-user.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-create-user',
  templateUrl: './create-user.component.html',
  styleUrls: ['./create-user.component.scss'],
})
export class CreateUserComponent implements OnInit {
  formGroup!: FormGroup;
  readonly CONSTANTS = CONSTANTS;
  orignalFormData: string | undefined;

  subscription: Subscription | undefined;
  readonly LANG_CONST = LANG_CONST;
  sharedData?: ISharedData;
  stepper = 0;
  skipStepUserType = false;
  skipStepBuilding = false;
  userGroup: IUserGroupDetails[] | undefined;
  selectedGroup: IUserGroupDetails[] | undefined;
  errorMessage: string | undefined;
  selectedUnitAccessPoint = {} as IUnitAndAccessPoint;
  selectedUnitId!: string;
  siteTimeZone: string | undefined;
  commonAccessControl!: boolean;
  selectedAccessPointId!: string;
  alreadySelectedGroups!: string[] | undefined;
  @ViewChild(UserDetailsComponent) userDetailRefComp:
    | UserDetailsComponent
    | undefined;
  @ViewChild(UserTypeComponent) userTypeRefComp: UserTypeComponent | undefined;
  @ViewChild(UserGroupComponent) UserGroupRefComp:
    | UserGroupComponent
    | undefined;
  @ViewChild(AccessTimeComponent) accessTimeRefComp:
    | AccessTimeComponent
    | undefined;
  @ViewChild(AccessDetailEmailComponent) accessDetailEmailRefComp:
    | AccessDetailEmailComponent
    | undefined;
  @ViewChild(VerifyEmailComponent) verifylEmailRefComp:
    | VerifyEmailComponent
    | undefined;

  /**
   * @description constructor injects all necesary services
   * @param { CreateUserComponent } dialogRef
   */
  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<CreateUserComponent>,
    private sharedDataService: SharedDataService,
    private userManagementService: UserManagementService,
    private notificationService: NotificationService,
    private helperService: HelperService,
    @Inject(MAT_DIALOG_DATA) private modalData: any,
    private buildingManagementService: BuildingManagementService,
    private addUserService: AddUserService,
    private translate: TranslateService
  ) {
    this.selectedUnitAccessPoint.units = [];
    this.selectedUnitAccessPoint.accessPoints = [];
  }

  /**
   *  @description call when initialize time do necessary feature add
   */
  ngOnInit(): void {
    this.siteTimeZone = this.modalData?.siteTimeZone;
    this.sharedDataService.currentData.subscribe((sharedData) => {
      this.sharedData = sharedData;
      this.selectedUnitId = sharedData?.selectedUnit?.unitId;
      this.selectedAccessPointId =
        sharedData?.selectedAccessPoint?.accessPointId;
      this.commonAccessControl = sharedData.selectedSite.commonAccessControl;
      this.addUserService.resetAll();
    });

    this.createControls();
  }

  /**
   * @description Create all child component into parents
   */
  createControls(): void {
    this.formGroup = this.fb.group({
      userDetails: this.fb.group({
        firstName: [
          '',
          MFValidators.compose([
            MFValidators.required,
            MFValidators.whitespace,
            MFValidators.maxLength(32),
            MFValidators.nameWithAllowedSpecialChars,
          ]),
        ],
        lastName: [
          '',
          MFValidators.compose([
            MFValidators.required,
            MFValidators.whitespace,
            MFValidators.maxLength(32),
            MFValidators.nameWithAllowedSpecialChars,
          ]),
        ],
      }),
      userTypes: this.fb.group({
        userType: ['', MFValidators.compose([MFValidators.required])],
      }),
      accessTime: this.fb.group(
        {
          startTime: [''],
          expireTime: [''],
          repeat: [false],
        },
        {
          validator: [
            MFValidators.startTimeEndTime('startTime', 'expireTime'),
            MFValidators.startTimeSiteZoneValidator(
              'startTime',
              this.siteTimeZone!
            ),
            MFValidators.siteZoneValidator('expireTime', this.siteTimeZone!),
          ],
        }
      ),
      accessEmail: this.fb.group({
        email: [
          '',
          MFValidators.compose([MFValidators.required, MFValidators.email]),
        ],
        region: [
          '',
          MFValidators.compose([MFValidators.required, MFValidators.region]),
        ],
        phone: [
          '',
          MFValidators.compose([MFValidators.required, MFValidators.usCaPhone]),
        ],
      }),
      Email: this.fb.group({
        existingUserId: [''],
        email: [
          '',
          MFValidators.compose([MFValidators.required, MFValidators.email]),
        ],
      }),
    });

    // default value set reference ticket# MPC-5978
    this.formGroup?.controls?.accessEmail?.patchValue({
      region: CONSTANTS.PHONE_NUMBER_REGION,
    });
    this.formGroup.get('accessEmail.region')?.disable();

    // in case form state create mode
    if (!this.modalData.isEditable)
      this.orignalFormData = JSON.stringify(this.formGroup.value);
  }
  submitToParent(): void {
    this.formGroup.controls.userDetails.enable();
    this.formGroup.controls.accessEmail.enable();
    this.formGroup.controls.userTypes.enable();
    let userDetail: IShareAccess | any = {};
    if (this.userGroup) {
      userDetail.userGroups = this.userGroup?.map(
        (userGroup: any) => userGroup?.userGroupId
      );
    }

    if (!this.formGroup.value?.Email?.existingUserId) {
      userDetail.email = this.formGroup.value?.accessEmail?.email;
      userDetail.phone = `${this.formGroup.value?.accessEmail?.region}${this.formGroup.value?.accessEmail?.phone}`;
      userDetail.phone = transformPhone(userDetail.phone);
      userDetail.firstName = this.formGroup.value?.userDetails?.firstName;
      userDetail.lastName = this.formGroup.value?.userDetails?.lastName;
      userDetail.userType = this.formGroup.value?.userTypes?.userType;
    } else {
      userDetail.existingUserId = this.formGroup.value?.Email?.existingUserId;
    }

    userDetail.userType = this.formGroup.value?.userTypes?.userType;

    if (this.formGroup.value?.accessTime?.repeat) {
      const userStartTimeEndTime = this.startTimeEndTimeUpdate();
      userDetail.repeatStartTime = userStartTimeEndTime.repeatStartTime;
      userDetail.repeatEndTime = userStartTimeEndTime.repeatEndTime;

      userDetail.repeatDays = this.formGroup.value?.accessTime?.repeatDays;
    } else {
      this.formGroup.value?.accessTime?.startTime
        ? (userDetail.startTime = convertParseDateTime(
            this.formGroup.value?.accessTime?.startTime,
            "EEE, MMM do, yyyy 'at' h:mm a",
            "yyyy-MM-dd'T'HH:mm:ss"
          ))
        : (userDetail.startTime = CONSTANTS.NOW);

      this.formGroup.value?.accessTime?.expireTime
        ? (userDetail.expireTime = convertParseDateTime(
            this.formGroup.value?.accessTime?.expireTime,
            "EEE, MMM do, yyyy 'at' h:mm a",
            "yyyy-MM-dd'T'HH:mm:ss"
          ))
        : null;
    }

    if (this.sharedData?.selectedSite?.siteId) {
      userDetail.siteId = this.sharedData?.selectedSite?.siteId as string;
      if (
        userDetail.userType &&
        userTypeResponse[userDetail.userType] !==
          CONSTANTS.PROPERTY_MANAGER_ADMIN &&
        userTypeResponse[userDetail.userType] !==
          CONSTANTS.PROPERTY_MANAGER_USER
      ) {
        userDetail.entities = [
          {
            unitId: this.sharedData?.selectedUnit?.unitId ?? null,
            accessPointId:
              this.sharedData?.selectedAccessPoint?.accessPointId ?? null,
            floorId: this.sharedData?.selectedFloor?.floorId,
            buildingId: this.sharedData?.selectedBuilding?.buildingId,
          },
        ];
      } else {
        userDetail.entities = [];
      }
    }

    this.userManagementService.shareAccess(userDetail).subscribe({
      next: () => {
        this.stepper += 1;
        if (this.selectedUnitId) {
          this.buildingManagementService
            .getUserViaUnit(this.selectedUnitId)
            .subscribe();
        } else {
          this.buildingManagementService
            .getUserViaAccessPoint(this.selectedAccessPointId)
            .subscribe();
        }
      },
      error: (err: Error) => {
        this.sharedDataService.checkFlag(true);
        if (window.__dev__) console.log('submitToParent', err);
        if (this.formGroup.value?.Email?.existingUserId) {
          this.formGroup.controls.userDetails.disable();
          this.formGroup.controls.accessEmail.disable();
          this.formGroup.controls.userTypes.disable();
        }
        this.notificationService.showError(err.message);
      },
    });
  }

  /**
   * @description this function responsible of existing user list when select any user execute this function
   * @return void
   * @param user user object
   */
  currentUserEmitter(user: IUser): void {
    if (user?.groupIds) {
      this.alreadySelectedGroups = [...user.groupIds!];
    }
    let name = user.name?.split(' ') || '';
    if (this.formGroup.value?.Email?.existingUserId) {
      this.formGroup.controls?.userDetails.patchValue({
        firstName: name[0],
        lastName: name[1],
      });
      this.formGroup.controls?.accessEmail.patchValue({
        email: user.email,
      });
      if (typeof user.userType === 'string') {
        this.formGroup.controls?.userTypes.patchValue({
          userType: UserTypeRequest[this.translate.instant(user.userType!)],
        });
      } else {
        this.formGroup.controls?.userTypes.patchValue({
          userType: user.userType!,
        });
      }

      this.setPhoneRegionEmail(user.phoneNumber!, user.email!);
      this.formGroup.controls.userDetails.disable();
      this.formGroup.controls.accessEmail.disable();

      this.formGroup.controls.userTypes.disable();
    } else {
      resetForm(this.formGroup.controls.accessEmail as FormGroup, '');
      this.formGroup.controls?.accessEmail.patchValue({
        region: CONSTANTS.PHONE_NUMBER_REGION,
      });
      this.formGroup.controls?.accessEmail.enable();
    }
  }

  /**
   * @description form submit get all fromgroup reference here to transform data
   * as per server object requirement
   * @returns void
   */
  startTimeEndTimeUpdate(): IShareAccess {
    const userDetail: IShareAccess = {};

    if (
      !(
        this.formGroup.value?.accessTime?.repeatStartTime &&
        this.formGroup.value?.accessTime?.repeatEndTime
      )
    ) {
      // default value MPC-6375
      userDetail.repeatStartTime = convertParseDateTime(
        '12:00 AM',
        'hh:mm a',
        'HH:mm'
      );
      userDetail.repeatEndTime = convertParseDateTime(
        '11:59 PM',
        'hh:mm a',
        'HH:mm'
      );
    } else {
      userDetail.repeatStartTime = convertParseDateTime(
        this.formGroup.value?.accessTime?.repeatStartTime,
        'hh:mm a',
        'HH:mm'
      );

      userDetail.repeatEndTime = convertParseDateTime(
        this.formGroup.value?.accessTime?.repeatEndTime,
        'hh:mm a',
        'HH:mm'
      );
    }
    return userDetail;
  }

  /**
   * @description setPhoneRegionEmail function validates phone number and apply masking on the number.
   * @param phone {string}
   * @param email {string}
   * @returns void
   */
  setPhoneRegionEmail(phone: string, email: string): void {
    this.helperService.validatePhoneFormat(phone).subscribe(
      ({
        phoneNumber,
        region,
        code,
      }: {
        phoneNumber: string;
        region: string;
        code: string;
      }) => {
        this.formGroup.controls?.accessEmail.patchValue({
          phone: removeSpecialChar(phoneNumber),
          region: code,
          email,
        });
      },
      (err: string) => {
        if (window.__dev__) console.log('setPhoneNumber', err);
      }
    );
  }

  /**
   * @description reset all child form and  states
   * @returns void
   */
  formReset(): void {
    this.formGroup.reset();
    resetForm(this.formGroup.get('accessEmail') as FormGroup, '');
    this.formGroup.get('accessEmail.region')?.disable();
    this.userDetailRefComp?.removeUser();
    this.verifylEmailRefComp?.removeUser();
    this.verifylEmailRefComp?.isFlag.next(true);
    this.userGroup = undefined;
    this.selectedGroup = undefined;
    this.skipStepUserType = false;
    this.skipStepBuilding = false;
    this.sharedDataService.checkFlag(true);
    (this.formGroup.get('accessTime') as FormGroup).patchValue({
      repeat: false,
    });
    (this.formGroup.get('accessTime') as FormGroup).removeControl(
      'repeatStartTime'
    );
    (this.formGroup.get('accessTime') as FormGroup).removeControl(
      'repeatEndTime'
    );
    (this.formGroup.get('accessTime') as FormGroup).removeControl('repeatDays');
    this.stepper = 0;
  }

  /**
   * @description close modal popup
   * @returns void
   */
  closeDialog() {
    this.sharedDataService.checkFlag(false);
    if (this.orignalFormData === JSON.stringify(this.formGroup.value)) {
      this.dialogRef?.close();
      return;
    }

    const dialogInstance = this.helperService.openModalDialog(
      ConfirmDialogComponent,
      {
        title: this.translate.instant('TITLE'),
        headingText: this.translate.instant('HEADING_TEXT'),
        bodyText: this.translate.instant('BODY_TEXT'),
        footerText: this.translate.instant('FOOTER_TEXT'),
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
    dialogInstance.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.dialogRef?.close();
      }
    });
  }
}
